import React, { useState } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Business } from '@mui/icons-material';
import { useCompany } from '../../contexts/CompanyContext';

const CompanySelector: React.FC = () => {
  const { companies, selectedCompany, isLoading, setSelectedCompany } = useCompany();

  const handleCompanyChange = (event: SelectChangeEvent<number>) => {
    const companyId = event.target.value as number;
    const company = companies.find(c => c.id === companyId);
    if (company) {
      setSelectedCompany(company);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading companies...</Typography>
      </Box>
    );
  }

  if (companies.length === 0) {
    return (
      <Alert severity="warning" sx={{ maxWidth: 300 }}>
        No companies available. Please contact your administrator.
      </Alert>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Business sx={{ color: 'white' }} />
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <Select
          value={selectedCompany?.id || ''}
          onChange={handleCompanyChange}
          displayEmpty
          sx={{ 
            color: 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'white',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            },
          }}
          renderValue={(value) => {
            if (!value) {
              return <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Select Company</Typography>;
            }
            const company = companies.find(c => c.id === value);
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" fontWeight="medium" sx={{ color: 'white' }}>
                  {company?.name}
                </Typography>
                <Chip 
                  label={company?.city} 
                  size="small" 
                  variant="outlined"
                  sx={{ 
                    height: 20, 
                    fontSize: '0.7rem',
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.5)'
                  }}
                />
              </Box>
            );
          }}
        >
          {companies.map((company) => (
            <MenuItem key={company.id} value={company.id}>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {company.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {company.city}, {company.postcode} • {company.employee_count} employees
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default CompanySelector; 